/* oxlint-disable max-lines, import/max-dependencies -- lifecycle orchestrator, splitting would scatter tightly-coupled control flow; the WorkPreservation import pushes it one over the dependency cap */
import type {
  AgentRunnerStatus,
  AgentMode,
  RunnerMode,
  TaskContext,
  TaskContextDTO,
} from "@project/shared";
import { DEFAULT_SONNET_MODEL, PRE_BUILD_TASK_STATUSES } from "@project/shared";
import {
  AgentConnection,
  type AgentConnectionConfig,
  type IncomingMessage,
} from "../connection/agent-connection.js";
import { ModeController, type ModeTaskContext } from "./mode-controller.js";
import { Lifecycle, DEFAULT_LIFECYCLE_CONFIG, type LifecycleConfig } from "./lifecycle.js";
import { QueryBridge } from "./query-bridge.js";
import type { AgentRunnerConfig } from "../runner-types.js";
import {
  syncWithBaseBranch,
  ensureOnTaskBranch,
  flushAllPendingWork,
  restoreWipSnapshot,
  updateRemoteToken,
} from "./git-utils.js";
import { mapChatHistory, readAgentVersion } from "./session-runner-helpers.js";
import { sampleKeyUsage } from "../execution/usage-sampler.js";
import { awaitGitReady } from "../setup/git-ready.js";
import { PortDiscovery } from "./port-discovery.js";
import { restoreOnBoot, uploadSnapshotToGcs, finalizeForSleep } from "./work-preservation/index.js";
import { handlePullBranch } from "./parent-pull-handler.js";
import { isHeavyGateActive } from "./heavy-gate.js";
import { LoopLagMonitor, type LoopStatus } from "../connection/loop-lag.js";
import { ensureClaudeCredentials, removeConveyorCredentials } from "../harness/pty/credentials.js";

// ── Configuration ──────────────────────────────────────────────────────────

/** Modes whose initial instructions auto-run (submit) at session start. */
const AUTO_RUN_MODES = new Set<string>(["building", "auto", "review", "discovery", "chat"]);

export interface SessionRunnerConfig {
  connection: AgentConnectionConfig;
  agentMode?: AgentMode;
  runnerMode?: RunnerMode;
  isAuto?: boolean;
  workspaceDir: string;
  model?: string;
  lifecycle?: Partial<LifecycleConfig>;
}

export interface SessionRunnerCallbacks {
  onStatusChange: (status: AgentRunnerStatus) => void | Promise<void>;
  onEvent: (event: Record<string, unknown>) => void | Promise<void>;
}

export interface SessionRunnerPortDiscovery {
  start(): Promise<void>;
  stop(): void;
}

export interface SessionRunnerDependencies {
  portDiscovery?: SessionRunnerPortDiscovery;
}

// ── SessionRunner: main lifecycle orchestrator ─────────────────────────────

export class SessionRunner {
  readonly connection: AgentConnection;
  readonly mode: ModeController;
  readonly lifecycle: Lifecycle;

  private readonly config: SessionRunnerConfig;
  private readonly callbacks: SessionRunnerCallbacks;
  private _state: AgentRunnerStatus = "connecting";
  private stopped = false;
  private interrupted = false;
  /** Defense-in-depth: set when the agent emits a "completed" event.
   *  Prevents the core loop from processing any further messages. */
  private completedThisTurn = false;
  /** Absolute deadline (ms epoch) at which the dormant idle wait must time
   *  out. Set on the FIRST entry into dormant idle for a given completion
   *  cycle and preserved across iterations so inbound critical messages
   *  cannot extend the bound past the configured `dormantTimeoutMs`. Reset
   *  to null when the agent transitions out of dormant idle (a wake actually
   *  promotes it back to a working turn). */
  private dormantDeadline: number | null = null;

  private taskContext: ModeTaskContext | null = null;
  private fullContext: TaskContext | null = null;
  private queryBridge: QueryBridge | null = null;
  private inputResolver: ((msg: IncomingMessage | null) => void) | null = null;
  private pendingMessages: IncomingMessage[] = [];
  /** Guards overlapping runs of the periodic git flush. */
  private periodicFlushInFlight = false;
  /** Consecutive flush ticks skipped because a heavy gate was running. */
  private gateSkipCount = 0;
  /** Max consecutive gate-deferred ticks (~2 min each) before flushing anyway. */
  private static readonly MAX_GATE_SKIPS = 10;
  /** Runtime preview-port poller (v3 dynamic port discovery). */
  private readonly portDiscovery: SessionRunnerPortDiscovery;
  /** Main event-loop lag measurement, shared with the heartbeat worker. */
  private readonly loopLag = new LoopLagMonitor();

  constructor(
    config: SessionRunnerConfig,
    callbacks: SessionRunnerCallbacks,
    deps: SessionRunnerDependencies = {},
  ) {
    this.config = config;
    this.callbacks = callbacks;

    // Compose components
    this.connection = new AgentConnection(config.connection);
    this.portDiscovery =
      deps.portDiscovery ??
      new PortDiscovery({
        report: (ports) => this.connection.reportDiscoveredPorts(ports),
      });

    const initialMode =
      config.agentMode ??
      (config.runnerMode === "pm" ? (config.isAuto ? "auto" : "discovery") : "building");
    this.mode = new ModeController(initialMode, config.runnerMode, config.isAuto);

    const lifecycleConfig = { ...DEFAULT_LIFECYCLE_CONFIG, ...config.lifecycle };
    this.lifecycle = new Lifecycle(lifecycleConfig, {
      onHeartbeat: () => this.connection.sendHeartbeat(this.loopLag.takeMaxLagMs()),
      onIdleTimeout: () => {
        process.stderr.write("[conveyor-agent] Idle timeout reached, stopping agent\n");
        this.stopped = true;
        // A prefilled initial query parks inside executeQuery (no
        // inputResolver) — abort it so the wait actually ends.
        this.queryBridge?.stop();
        if (this.inputResolver) {
          const resolver = this.inputResolver;
          this.inputResolver = null;
          resolver(null);
        }
      },
      onDormantTimeout: () => {
        process.stderr.write("[conveyor-agent] Dormant idle timeout reached, shutting down\n");
        this.stopped = true;
        this.queryBridge?.stop();
        if (this.inputResolver) {
          const resolver = this.inputResolver;
          this.inputResolver = null;
          resolver(null);
        }
      },
      onTokenRefresh: () => void this.refreshGithubToken(),
      onGitFlush: () => void this.periodicGitFlush(),
      onUsageSample: () => void this.sampleAndReportKeyUsage(),
    });
  }

  get state(): AgentRunnerStatus {
    return this._state;
  }

  get sessionId(): string {
    return this.connection.sessionId;
  }

  get isStopped(): boolean {
    return this.stopped;
  }

  // ── Main lifecycle ─────────────────────────────────────────────────

  /**
   * Establish the API connection, wire callbacks, and join the session room.
   * Call this before run() when you need to send events (e.g. setup/start
   * command output) before the main agent lifecycle begins.
   */
  async connect(): Promise<boolean> {
    await this.setState("connecting");

    // 1. Connect
    await this.connection.connect();
    await this.setState("connected");
    this.connection.sendEvent({ type: "connected", sessionId: this.sessionId });

    // 2. Wire callbacks
    this.wireConnectionCallbacks();
    this.lifecycle.startHeartbeat();
    // Loop-lag measurement + the starvation-proof heartbeat worker: a stalled
    // main loop must not silence liveness (lease TTL 150s + 120s grace) — the
    // worker keeps beating with `loopLagMs` so the API can tell busy from dead.
    this.loopLag.start();
    this.connection.startHeartbeatWorker(this.loopLag.sharedBuffer);
    this.lifecycle.startTokenRefresh();
    // Report the running subscription key's usage right away, then periodically.
    // Read-only `claude -p "/usage"` subprocess — safe to start pre-`run()`,
    // unlike the git-flush timer below.
    this.lifecycle.startUsageSample();
    // NOTE: the periodic git-flush timer is NOT started here. It touches the
    // repo (git add/stash/reset + force-push conveyor-wip/<branch>) with no
    // git-ready guard of its own, so starting it before `run()`'s
    // `awaitGitReady` gate resolves could race entrypoint.sh's backgrounded
    // checkout/merge (index.lock contention, or a force-push over a
    // not-yet-restored WIP snapshot). It is started in `run()` instead, only
    // once git prep is confirmed ready.

    // 2.5. Join session room and retrieve pending messages
    const { pendingMessages: serverMessages } = await this.connection.call("connectAgent", {
      sessionId: this.sessionId,
    });
    for (const msg of serverMessages) {
      if (msg.content) {
        this.pendingMessages.push({ content: msg.content, userId: msg.userId });
      }
    }

    // 2.55. Establish the preview-port baseline before releasing the caller to
    // launch setup/start commands. Anything already listening (sshd 2222,
    // sidecars, agent internals) belongs in the baseline; application ports
    // opened by workspace commands must be observed by a later poll instead.
    // Unsupported hosts remain non-fatal, but a stop while the baseline is
    // pending closes this startup gate so callers cannot launch commands.
    if (this.stopped) return false;
    try {
      await this.portDiscovery.start();
    } catch {
      // Best effort — a host without /proc/net/tcp disables preview discovery.
    }
    if (this.stopped) return false;

    // 2.6. Fire-and-forget: tell the API our agent version. If a newer
    // @rallycry/conveyor-agent is on NPM, the API will queue a base image
    // rebuild for the project (deduped per-project across concurrent agents).
    // We continue running with the current version regardless.
    const agentVersion = readAgentVersion();
    if (agentVersion) {
      this.connection
        .call("notifyAgentVersion", { sessionId: this.sessionId, agentVersion })
        .catch(() => {
          // Best-effort — don't block agent startup if NPM lookup or rebuild fails
        });
    }
    return true;
  }

  /**
   * Run the main agent lifecycle: fetch context, resolve mode, execute, loop.
   * Requires connect() to have been called first.
   */
  // oxlint-disable-next-line max-lines-per-function, complexity -- lifecycle orchestration is inherently sequential
  async run(): Promise<void> {
    // 3. Fetch context
    await this.setState("fetching_context");
    try {
      const ctx = await this.connection.call("getTaskContext", {
        sessionId: this.sessionId,
        includeHistory: true,
      });
      this.fullContext = this.buildFullContext(ctx);
      this.taskContext = {
        status: ctx.status,
        plan: ctx.plan,
        storyPointId:
          ctx.storyPoints === null || ctx.storyPoints === undefined
            ? null
            : String(ctx.storyPoints),
        model: ctx.model,
        githubPRUrl: ctx.githubPRUrl,
        isParentTask: this.fullContext.isParentTask,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to fetch task context";
      this.connection.sendEvent({ type: "error", message });
      await this.callbacks.onEvent({ type: "error", message });
      await this.shutdown("error");
      return;
    }

    // 3.5. Initial git setup. In claudespace v3, entrypoint.sh runs the
    // fetch + checkout + merge in the BACKGROUND (so the card lights up before
    // git finishes) and signals completion via a marker file. Gate Claude on
    // that marker: it must never spawn on a stale branch. `awaitGitReady`
    // returns "not-gated" immediately when no marker is configured (GitHub
    // Codespaces / local), preserving those environments' own git-sync below.
    const gitState = await awaitGitReady({
      onLog: (m) => process.stderr.write(`[conveyor-agent] ${m}\n`),
    });
    if (gitState === "failed" || gitState === "timeout") {
      const message =
        gitState === "failed"
          ? "Workspace git preparation failed (see pod logs)"
          : "Workspace git preparation timed out (see pod logs)";
      this.connection.sendEvent({ type: "error", message });
      await this.callbacks.onEvent({ type: "error", message });
      // Do NOT spawn Claude on a broken/absent repo — mirror the context-fetch
      // failure handling above.
      await this.shutdown("error");
      return;
    }

    // Entrypoint sets CONVEYOR_GIT_READY=1 when bash owns the git block. For
    // GitHub Codespaces / local dev (not gated), fall through and sync here.
    // This in-line checkout/merge must run BEFORE the WIP flush timer is armed
    // (below): the flush takes .git/index.lock and force-pushes
    // conveyor-wip/<branch> with no git-ready guard of its own, so a tick
    // firing mid-`git checkout -B` / merge would contend for index.lock —
    // exactly the race the git-ready reordering was meant to prevent.
    if (process.env.CONVEYOR_GIT_READY !== "1") {
      if (this.fullContext?.githubBranch) {
        await ensureOnTaskBranch(this.config.workspaceDir, this.fullContext.githubBranch);
      }
      if (this.fullContext?.baseBranch) {
        await syncWithBaseBranch(this.config.workspaceDir, this.fullContext.baseBranch);
      }
    }

    // Git prep is confirmed ready — the gated (v3) path had its entrypoint git
    // block complete before CONVEYOR_GIT_READY=1 (the invariant awaitGitReady
    // waits on), and the non-gated in-line checkout/merge above has now
    // finished — so it is finally safe to arm the periodic WIP flush timer.
    // Starting it any earlier (e.g. in connect(), or before the block above)
    // races either entrypoint.sh's backgrounded checkout/merge or the in-line
    // sync: the flush takes .git/index.lock and force-pushes
    // conveyor-wip/<branch> with no git-ready guard of its own. Skip arming it
    // if the runner is already stopping (e.g. idle/dormant timeout fired while
    // we were gated on git). `startGitFlush()` internally stops any prior timer
    // first, so even if this were reached twice there is no double-start.
    if (!this.stopped) {
      this.lifecycle.startGitFlush();
    }

    // 3.6. Recover uncommitted work a previous pod snapshotted before dying.
    // v3 pods (CONVEYOR_SNAPSHOT_URL from the bootstrap bundle) restore with
    // GCS-then-wip precedence via WorkPreservation; otherwise the wip ref
    // alone (the parent-HEAD guard makes a stale snapshot a no-op).
    if (this.fullContext?.githubBranch) {
      const snapshotUrl = process.env.CONVEYOR_SNAPSHOT_URL;
      if (snapshotUrl) {
        const restore = await restoreOnBoot(
          { snapshotUrl, gitPlan: { branch: this.fullContext.githubBranch } },
          this.config.workspaceDir,
        );
        if (restore.source !== "clean") {
          process.stderr.write(
            `[conveyor-agent] WorkPreservation restore: source=${restore.source}${
              restore.fileCount === undefined ? "" : ` files=${restore.fileCount}`
            }\n`,
          );
        }
      } else {
        const restored = await restoreWipSnapshot(
          this.config.workspaceDir,
          this.fullContext.githubBranch,
        );
        if (restored !== "none") {
          process.stderr.write(`[conveyor-agent] WIP snapshot restore: ${restored}\n`);
        }
      }
    }

    // 4. Reconcile mode from server context (overrides env-var defaults)
    this.mode.applyServerMode(this.fullContext?.agentMode, this.fullContext?.isAuto);
    this.mode.resolveInitialMode(this.taskContext);

    // 4.1. Auto tasks are always build-capable at boot (auto has no plan
    // turn), so every fresh auto card in a pre-build status gets its
    // InProgress advance + identification fired here — belt-and-braces for
    // any kickoff path that missed the server-side status bump.
    // Fire-and-forget — best-effort.
    if (
      this.fullContext?.isAuto &&
      PRE_BUILD_TASK_STATUSES.has(this.taskContext.status) &&
      this.mode.isBuildCapable
    ) {
      void this.connection.triggerIdentification().catch(() => {});
    }

    // 4.5. Create query bridge for SDK execution
    this.queryBridge = this.createQueryBridge();

    // 5. Log initialization
    this.logInitialization();

    // 6. Execute initial mode
    const staleBatch = [...this.pendingMessages];
    const didExecuteInitialQuery = await this.executeInitialMode();

    // Clear stale pending messages from connectAgent — they were already
    // included in the initial task context (chat history). Any messages
    // that arrived via live socket events DURING initial mode are not in
    // the snapshot, so they survive. Removal is by object identity because
    // executeInitialMode may itself consume/fold entries (prefill paths).
    // Only splice when the initial mode actually ran a query (auto/building/
    // review/pack/discovery/prefill) — a parked `help` prefill skips the query,
    // so its pending messages must be retained for the core loop to process. A
    // stale entry whose content is NOT in the chat-history snapshot (e.g. a
    // queued message that never got a chat row) is kept — silently dropping it
    // would lose the message; the core loop delivers it as a follow-up.
    if (staleBatch.length > 0 && didExecuteInitialQuery) {
      // Non-assistant roles mirror the connect-time chat scan's filter — a
      // pending message always originates from a user/system chat row.
      const historyContents = new Set(
        (this.fullContext?.chatHistory ?? [])
          .filter((m) => m.role !== "assistant" && m.content.trim())
          .map((m) => m.content.trim()),
      );
      for (const stale of staleBatch) {
        if (stale.content.trim() && !historyContents.has(stale.content.trim())) continue;
        const idx = this.pendingMessages.indexOf(stale);
        if (idx !== -1) this.pendingMessages.splice(idx, 1);
      }
    }

    // Discovery ExitPlanMode during the (prefilled) initial query: planning is
    // done and the runner falls through to the normal idle wait below. Clear
    // the flag so a later follow-up's completion isn't mistaken for a fresh
    // discovery completion (which would drain pending messages in coreLoop).
    if (this.queryBridge?.isDiscoveryCompleted) {
      process.stderr.write(
        "[conveyor-agent] Discovery completed during initial query — entering idle\n",
      );
      this.queryBridge.isDiscoveryCompleted = false;
    }

    // 7. Core loop: idle → message → execute → idle
    if (!this.stopped) {
      process.stderr.write(
        `[conveyor-agent] Listening for messages (mode: ${this.mode.effectiveMode})\n`,
      );
    }
    while (!this.stopped) {
      if (this._state !== "idle") await this.setState("idle");
      await this.coreLoop();
    }

    // 8. Shutdown
    await this.shutdown("finished");
  }

  /** Convenience wrapper: connect() then run(). */
  async start(): Promise<void> {
    if (!(await this.connect())) return;
    await this.run();
  }

  // ── Core loop ──────────────────────────────────────────────────────

  // oxlint-disable-next-line complexity -- dormant idle paths add branches but are tightly coupled to the loop
  private async coreLoop(): Promise<void> {
    while (!this.stopped) {
      // Defense-in-depth: if the agent already emitted a "completed" event,
      // stop the loop.  The server-side guard should prevent messages from
      // reaching us, but if one leaks through, this prevents the completion
      // cycle (agent says "done" → system message → agent says "done" → …).
      if (this.completedThisTurn) {
        const resumed = await this.handleDormantIdle();
        if (!resumed) break;
        continue;
      }
      if (this._state === "idle") {
        this.lifecycle.startIdleTimer();
        const msg = await this.waitForMessage();
        this.lifecycle.cancelIdleTimer();

        if (!msg) {
          if (this.interrupted && !this.stopped) {
            this.interrupted = false;
            continue;
          }
          break;
        }

        this.interrupted = false;

        if (msg.source === "pty_passive") {
          // Keep-alive PTY: a human typed into the parked, idle Connected-TUI.
          // Drain the passive turn (no prompt fed, no synthetic user_message) so
          // status flips to running and the exchange mirrors into chat.
          await this.setState("running");
          await this.executePassive();
        } else {
          await this.callbacks.onEvent({
            type: "user_message",
            content: msg.content,
            userId: msg.userId,
          });

          // Execute the Claude SDK query with the user's message. A hinted
          // refine message parks in the TUI input for the human instead of
          // auto-submitting.
          if (this.prefillEligible(msg)) {
            await this.runPrefilledMessage(msg, this.mode.effectiveMode);
          } else {
            await this.setState("running");
            await this.executeQuery(msg.content);
          }
        }

        // Discovery ExitPlanMode: agent completed its plan — shut down cleanly.
        // No idle state, no message waiting, no chance of being re-woken.
        if (this.queryBridge?.isDiscoveryCompleted) {
          process.stderr.write(
            "[conveyor-agent] Discovery completed — entering dormant idle (staying connected)\n",
          );
          this.pendingMessages.length = 0;
          if (this._state !== "idle") await this.setState("idle");
          const discoveryMsg = await this.waitForMessage();
          if (!discoveryMsg) break;
          process.stderr.write(
            "[conveyor-agent] Received message while discovery-dormant, resuming\n",
          );
          this.queryBridge.isDiscoveryCompleted = false;
          this.completedThisTurn = false;
          this.pendingMessages.unshift(discoveryMsg);
          continue;
        }

        if (this.stopped) break;
        if (this.interrupted) {
          this.interrupted = false;
          continue;
        }

        if (!this.stopped) await this.setState("idle");
      } else if (this._state === "error") {
        await this.setState("idle");
      } else {
        break;
      }
    }
  }

  /**
   * Handle dormant-after-completed idle. Returns true if a critical message
   * woke us (caller should `continue` the loop), false if we should break
   * (stop signal or dormant timeout fired).
   *
   * The absolute deadline is set on first entry per completion cycle and
   * preserved across re-entries: an inbound critical message cannot extend
   * the bound past `dormantTimeoutMs`. Only a genuine wake (clearing
   * `completedThisTurn`) resets the deadline so the next dormant entry
   * starts a fresh window.
   */
  private async handleDormantIdle(): Promise<boolean> {
    if (this.dormantDeadline === null) {
      this.dormantDeadline = Date.now() + this.lifecycle.config.dormantTimeoutMs;
      process.stderr.write(
        "[conveyor-agent] Completed — entering dormant idle (staying connected)\n",
      );
    }
    this.pendingMessages.length = 0;
    if (this._state !== "idle") await this.setState("idle");
    const remainingMs = Math.max(0, this.dormantDeadline - Date.now());
    this.lifecycle.startDormantTimer(remainingMs);
    const dormantMsg = await this.waitForMessage();
    this.lifecycle.cancelDormantTimer();
    if (!dormantMsg) return false;
    const contentPreview =
      dormantMsg.content.length > 80 ? `${dormantMsg.content.slice(0, 80)}...` : dormantMsg.content;
    process.stderr.write(
      `[conveyor-agent] Received message while dormant, resuming: ` +
        `userId=${dormantMsg.userId}, source=${dormantMsg.source || "unknown"}, ` +
        `content="${contentPreview.replace(/\n/g, "\\n")}"\n`,
    );
    this.completedThisTurn = false;
    this.dormantDeadline = null;
    this.pendingMessages.unshift(dormantMsg);
    return true;
  }

  // ── Initial mode execution ─────────────────────────────────────────

  /** Returns true if an initial query was executed, false otherwise. */
  private async executeInitialMode(): Promise<boolean> {
    if (!this.taskContext || !this.fullContext) return false;
    const effectiveMode = this.mode.effectiveMode;

    const intrinsicDelivery = this.queryBridge?.initialPromptDelivery(this.fullContext) ?? "submit";

    // A single hinted message (Refine → Discovery/Review while the pod was
    // down) parks in the TUI input instead of auto-submitting.
    const hinted = this.prepareInitialPendingMessages(intrinsicDelivery);
    if (hinted) {
      await this.runPrefilledMessage(hinted, effectiveMode);
      return true;
    }

    // Remaining pending chat messages already direct the agent (they arrived
    // before it booted) — force submit semantics so they aren't parked behind
    // an unsubmitted prefill: building/review run the initial query as before,
    // discovery falls through to the core loop which processes them.
    const delivery = this.pendingMessages.length > 0 ? "submit" : intrinsicDelivery;

    // Submit modes auto-run their initial instructions. Interactive discovery
    // now auto-submits too: a fresh interactive card presses Enter on its
    // planner preamble instead of parking an empty TUI. Prefill (PTY task chat,
    // fresh session) still spawns a parked TUI for `help` — the only remaining
    // read-only mode that waits on the human.
    const shouldRun = AUTO_RUN_MODES.has(effectiveMode) || delivery === "prefill";

    if (!shouldRun) {
      await this.setState("idle");
      return false;
    }

    if (delivery === "prefill") {
      // The query parks until the human submits in the TUI. Report
      // waiting_for_input (not running) and bound the wait with the idle
      // timer; the first harness event flips state to running via the
      // onStatusChange hook in createQueryBridge, cancelling the timer.
      await this.setState("waiting_for_input");
      await this.callbacks.onEvent({ type: "execute_mode", mode: effectiveMode, delivery });
      // A chat message may have raced the awaits above — let coreLoop process
      // it instead of parking it behind an unsubmitted prefill.
      if (this.pendingMessages.length > 0) {
        if (!this.stopped) await this.setState("idle");
        return false;
      }
      this.lifecycle.startIdleTimer();
      try {
        await this.executeQuery(undefined, delivery);
      } finally {
        this.lifecycle.cancelIdleTimer();
      }
    } else {
      await this.setState("running");
      await this.callbacks.onEvent({ type: "execute_mode", mode: effectiveMode });
      await this.executeQuery(undefined, delivery);
    }
    if (!this.stopped) await this.setState("idle");
    return true;
  }

  // ── Prefill-delivery helpers ───────────────────────────────────────

  /**
   * Startup-queue preamble: the card's initial message reaches a fresh pod
   * twice — inside the task context (chat history) AND as a queued pending
   * message. When the intrinsic delivery is prefill (manual-mode fresh TUI)
   * or a hinted message is queued (Refine), drop those context-duplicates so
   * they don't force submit semantics — a genuinely-new live message still
   * does. Returns the lone hinted message to park, if any.
   */
  private prepareInitialPendingMessages(
    intrinsicDelivery: "submit" | "prefill",
  ): IncomingMessage | null {
    const hasHintedPending = this.pendingMessages.some((m) => m.delivery === "prefill");
    if (this.pendingMessages.length > 0 && (intrinsicDelivery === "prefill" || hasHintedPending)) {
      this.foldContextDuplicatePendingMessages();
    }
    return this.takePrefillHintedMessage();
  }

  /**
   * Drop pending messages whose content is already present as a user message
   * in the task-context chat history (the card's initial message is delivered
   * both ways). Hinted (prefill) messages are kept — they are consumed by the
   * dedicated prefill path.
   */
  private foldContextDuplicatePendingMessages(): void {
    const userContents = new Set(
      (this.fullContext?.chatHistory ?? [])
        .filter((m) => m.role === "user" && m.content.trim())
        .map((m) => m.content.trim()),
    );
    const kept = this.pendingMessages.filter(
      (m) => m.delivery === "prefill" || !m.content.trim() || !userContents.has(m.content.trim()),
    );
    const dropped = this.pendingMessages.length - kept.length;
    if (dropped > 0) {
      this.pendingMessages.length = 0;
      this.pendingMessages.push(...kept);
      process.stderr.write(
        `[conveyor-agent] Folded ${dropped} pending message(s) already present in chat context\n`,
      );
    }
  }

  /**
   * Honor a prefill hint only when it is unambiguous: PTY harness, a manual
   * (human-facing) agent mode, no other message queued behind it, and not an
   * automated-critical source. Everything else keeps submit semantics.
   */
  private prefillEligible(msg: IncomingMessage): boolean {
    if (msg.delivery !== "prefill") return false;
    if (this.pendingMessages.length > 0) return false;
    if (this.queryBridge?.harnessKind !== "pty") return false;
    // Auto cards must never park a prefill in the TUI — the whole point of auto
    // is to submit and keep working, and a parked prefill blocks the human from
    // typing a follow-up. Submit instead.
    if (this.mode.isAuto) return false;
    // "user" is the queue-flush default source; "mode_change" is the refine
    // wake. Anything else (system/ci_failure/review_trigger/…) must submit.
    if (msg.source && msg.source !== "mode_change" && msg.source !== "user") return false;
    // Manual modes only.
    const m = this.mode.effectiveMode;
    return m === "discovery" || m === "review" || m === "help";
  }

  /** Consume the sole pending message when it should park as a prefill. */
  private takePrefillHintedMessage(): IncomingMessage | null {
    if (this.pendingMessages.length !== 1) return null;
    const [first] = this.pendingMessages;
    if (!first || first.delivery !== "prefill") return null;
    this.pendingMessages.length = 0;
    if (this.prefillEligible(first)) return first;
    this.pendingMessages.push(first);
    return null;
  }

  /**
   * Park a message in the Connected-TUI input (prefill) and wait for the human
   * to submit. Empty content prefills the mode's initial prompt instead (the
   * latest user chat message — e.g. a Refine wake on a dormant agent). The
   * caller restores the idle state afterwards.
   */
  private async runPrefilledMessage(msg: IncomingMessage, effectiveMode: AgentMode): Promise<void> {
    await this.setState("waiting_for_input");
    await this.callbacks.onEvent({
      type: "execute_mode",
      mode: effectiveMode,
      delivery: "prefill",
    });
    this.lifecycle.startIdleTimer();
    try {
      await this.executeQuery(msg.content.trim() ? msg.content : undefined, "prefill");
    } finally {
      this.lifecycle.cancelIdleTimer();
    }
  }

  // ── Message waiting ────────────────────────────────────────────────

  private waitForMessage(): Promise<IncomingMessage | null> {
    if (this.pendingMessages.length > 0) {
      return Promise.resolve(this.pendingMessages.shift() ?? null);
    }
    return new Promise<IncomingMessage | null>((resolve) => {
      this.inputResolver = resolve;
    });
  }

  /** Inject a message (from connection callback or external source) */
  injectMessage(msg: IncomingMessage): void {
    if (this.inputResolver) {
      const resolve = this.inputResolver;
      this.inputResolver = null;
      resolve(msg);
    } else {
      this.pendingMessages.push(msg);
      // Interrupt running query so the new message is processed quickly.
      // waiting_for_input covers a prefilled-but-unsubmitted TUI: the chat
      // message supersedes the prefill (teardown + respawn as follow-up).
      if (this._state === "running" || this._state === "waiting_for_input") {
        this.queryBridge?.stop();
      }
    }
  }

  // ── Query execution with abort handling ────────────────────────────

  /** Run queryBridge.execute, swallowing abort errors from stop/softStop. */
  private async executeQuery(
    followUpContent?: string,
    promptDelivery?: "submit" | "prefill",
  ): Promise<void> {
    if (!this.fullContext || !this.queryBridge) return;
    try {
      await this.queryBridge.execute(this.fullContext, followUpContent, promptDelivery);
    } catch (err) {
      if (this.interrupted || this.stopped) {
        process.stderr.write("[conveyor-agent] Query aborted by stop/softStop signal\n");
        return;
      }
      throw err;
    }
  }

  /**
   * A human typed into the parked, idle Connected-TUI (keep-alive PTY). Wake the
   * core loop with a sentinel so it drains a passive turn. No-op once stopped.
   */
  private onPassiveTuiActivity(): void {
    if (this.stopped) return;
    this.injectMessage({ content: "", userId: "human", source: "pty_passive" });
  }

  /** Run queryBridge.executePassive, swallowing abort errors from stop/softStop. */
  private async executePassive(): Promise<void> {
    if (!this.fullContext || !this.queryBridge) return;
    try {
      await this.queryBridge.executePassive(this.fullContext);
    } catch (err) {
      if (this.interrupted || this.stopped) {
        process.stderr.write("[conveyor-agent] Passive turn aborted by stop/softStop signal\n");
        return;
      }
      throw err;
    }
  }

  // ── Stop / soft-stop ───────────────────────────────────────────────

  /** Periodic best-effort WIP commit + push during normal agent execution.
   *  Covers ungraceful pod termination (OOMKilled, node crash/eviction) where
   *  the preStop hook + SIGTERM flush don't get a chance to run. No-ops on a
   *  clean tree. Guarded so two ticks can't overlap. Never throws. */
  private async periodicGitFlush(): Promise<void> {
    if (this.periodicFlushInFlight || this.stopped) return;
    // A running heavy gate (test/typecheck/build via singleton.sh) owns the
    // disk and churns the tree — flushing mid-gate snapshots build garbage
    // while competing for the exact IO the gate is starving on. Defer to the
    // next tick, but never indefinitely: a wedged gate must not disable crash
    // protection, so after MAX_GATE_SKIPS consecutive skips we flush anyway.
    if (isHeavyGateActive() && this.gateSkipCount < SessionRunner.MAX_GATE_SKIPS) {
      this.gateSkipCount++;
      if (this.gateSkipCount === 1) {
        process.stderr.write("[conveyor-agent] Periodic git flush deferred — heavy gate running\n");
      }
      return;
    }
    this.gateSkipCount = 0;
    this.periodicFlushInFlight = true;
    try {
      const result = await flushAllPendingWork(this.config.workspaceDir, {
        wipMessage: "WIP: periodic auto-commit",
        refreshToken: async () => {
          try {
            const res = await this.connection.call("refreshGithubToken", {
              sessionId: this.connection.sessionId,
            });
            return res.token;
          } catch {
            return undefined;
          }
        },
      });
      if (result.hadWork) {
        process.stderr.write(
          `[conveyor-agent] Periodic git flush: branchesBackedUp=${result.branchesBackedUp} worktreesSnapshotted=${result.worktreesSnapshotted}\n`,
        );
      }
      // v3 GCS tier rides the SAME trigger as the git tier (one capture
      // cadence, two sinks — never two racing snapshotters).
      await this.uploadGcsSnapshotIfConfigured();
    } catch {
      // best effort — next tick will retry
    } finally {
      this.periodicFlushInFlight = false;
    }
  }

  /** Sample the running Claude subscription key's rate-limit utilization from
   *  the Claude CLI `/usage` command and report it as `rate_limit_update` events.
   *  The API (`persistRateLimitSnapshot`) attributes them to the key this session
   *  launched under, keeping User Settings + the PtY-tab usage widget fresh and
   *  `selectBestKey` rotation honest. Best-effort — never throws, no-op when the
   *  pod has no OAuth token (e.g. API-key projects). */
  private async sampleAndReportKeyUsage(): Promise<void> {
    if (this.stopped) return;
    const samples = await sampleKeyUsage(process.env.CLAUDE_CODE_OAUTH_TOKEN);
    for (const sample of samples) {
      this.connection.sendEvent({
        type: "rate_limit_update",
        rateLimitType: sample.rateLimitType,
        utilization: sample.utilization,
        status: sample.status,
        gauges: sample.gauges,
      });
    }
  }

  /** PUT the WorkPreservation snapshot tar to the bundle's capability URL
   *  when this pod is v3 (CONVEYOR_SNAPSHOT_UPLOAD_URL set). Never throws. */
  private async uploadGcsSnapshotIfConfigured(): Promise<void> {
    const uploadUrl = process.env.CONVEYOR_SNAPSHOT_UPLOAD_URL;
    if (!uploadUrl) return;
    try {
      const gcs = await uploadSnapshotToGcs(this.config.workspaceDir, uploadUrl);
      if (gcs.uploaded) {
        process.stderr.write(
          `[conveyor-agent] WorkPreservation GCS snapshot: files=${gcs.fileCount} bytes=${gcs.sizeBytes}\n`,
        );
      }
    } catch {
      // best effort — the wip tier already preserved this generation
    }
  }

  /** Best-effort WIP commit + push on shutdown so in-flight work isn't lost
   *  when a claudespace pod is killed. Must be called BEFORE stop() so the
   *  connection is still alive for token refresh. Never throws. */
  async flushGitOnShutdown(): Promise<void> {
    try {
      const result = await flushAllPendingWork(this.config.workspaceDir, {
        wipMessage: "WIP: auto-commit on conveyor-agent shutdown",
        refreshToken: async () => {
          try {
            const res = await this.connection.call("refreshGithubToken", {
              sessionId: this.connection.sessionId,
            });
            return res.token;
          } catch {
            return undefined;
          }
        },
      });
      if (result.hadWork) {
        process.stderr.write(
          `[conveyor-agent] Shutdown git flush: branchesBackedUp=${result.branchesBackedUp} worktreesSnapshotted=${result.worktreesSnapshotted}\n`,
        );
      }
      // Final GCS capture so a stopping v3 pod leaves a restorable snapshot.
      await this.uploadGcsSnapshotIfConfigured();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      process.stderr.write(`[conveyor-agent] Shutdown git flush failed: ${msg}\n`);
    }
  }

  stop(): void {
    this.stopped = true;
    this.queryBridge?.stop();
    // Keep-alive PTY: kill any parked/active CLI process (fire-and-forget — the
    // server also clears the ring on terminal session status, so a lost ended
    // signal here is covered).
    void this.queryBridge?.dispose().catch(() => {});
    this.portDiscovery.stop();
    this.loopLag.stop();
    this.lifecycle.destroy();
    this.connection.disconnect();
    if (this.inputResolver) {
      const resolver = this.inputResolver;
      this.inputResolver = null;
      resolver(null);
    }
  }

  softStop(): void {
    this.interrupted = true;
    this.queryBridge?.stop();
    // Only null-resolve if no flushed messages are already pending — a message
    // arriving via injectMessage() before the soft stop signal means the agent
    // should process it instead of discarding with a null resolution.
    if (this.inputResolver && this.pendingMessages.length === 0) {
      const resolver = this.inputResolver;
      this.inputResolver = null;
      resolver(null);
    }
  }

  // ── Context & bridge construction ────────────────────────────────

  // oxlint-disable-next-line complexity
  private buildFullContext(ctx: TaskContextDTO): TaskContext {
    const chatHistory = mapChatHistory(ctx.chatHistory);

    return {
      taskId: ctx.id,
      projectId: ctx.projectId ?? "",
      title: ctx.title,
      description: ctx.description,
      plan: ctx.plan,
      status: ctx.status,
      chatHistory,
      agentId: ctx.agentId ?? null,
      _runnerSessionId: this.sessionId,
      agentInstructions: ctx.agentInstructions ?? "",
      model: ctx.model,
      githubBranch: ctx.githubBranch ?? "",
      baseBranch: ctx.baseBranch ?? "",
      projectName: ctx.projectName ?? null,
      projectDescription: ctx.projectDescription ?? null,
      githubPRUrl: ctx.githubPRUrl,
      claudeSessionId: ctx.claudeSessionId ?? null,
      lastSeenMessageId: ctx.lastSeenMessageId ?? null,
      isParentTask: ctx.isParentTask ?? false,
      storyPoints: ctx.storyPoints ?? undefined,
      projectAgents: ctx.projectAgents ?? undefined,
      projectTags: ctx.projectTags ?? undefined,
      taskTagIds: ctx.taskTagIds ?? undefined,
      projectObjectives: ctx.projectObjectives ?? undefined,
      incidents: ctx.incidents ?? undefined,
      recentRelatedTasks: ctx.recentRelatedTasks ?? undefined,
      agentSettings: ctx.agentSettings ?? null,
      agentMode: ctx.agentMode ?? undefined,
      isAuto: ctx.isAuto,
    };
  }

  private createQueryBridge(): QueryBridge {
    const runnerConfig: AgentRunnerConfig = {
      conveyorApiUrl: this.config.connection.apiUrl,
      taskToken: this.config.connection.taskToken,
      taskId: this.fullContext?.taskId ?? "",
      model: this.fullContext?.model ?? DEFAULT_SONNET_MODEL,
      instructions: this.fullContext?.agentInstructions ?? "",
      workspaceDir: this.config.workspaceDir,
      mode: this.config.runnerMode,
      isAuto: this.config.isAuto,
    };

    const bridge = new QueryBridge(this.connection, this.mode, runnerConfig, {
      onStatusChange: (status) => {
        // A prefilled initial query reports running when the human submits
        // in the TUI (first harness event) — leave waiting_for_input and
        // stop the prefill-wait idle timer so a long working turn isn't
        // killed by it.
        if (status === "running" && this._state === "waiting_for_input") {
          this._state = "running";
          this.lifecycle.cancelIdleTimer();
        }
        return this.callbacks.onStatusChange(status as AgentRunnerStatus);
      },
      onEvent: (event) => {
        // Track completion for the agent-side guard — once the agent emits
        // "completed", the core loop will stop accepting further messages.
        if ((event as Record<string, unknown>).type === "completed") {
          this.completedThisTurn = true;
          // Immediately refresh the server's heartbeat timestamp so
          // lastHeartbeatAt can't drift stale while the agent sits dormant
          // waiting for a critical wake-up (e.g. review_trigger). The
          // periodic 30s timer would otherwise leave up to a ~29s gap
          // between the `completed` event and the next heartbeat.
          void this.connection.sendHeartbeat();
        }
        return this.callbacks.onEvent(event);
      },
    });

    bridge.isParentTask = this.fullContext?.isParentTask ?? false;
    bridge.onSoftStop = () => {
      process.stderr.write("[conveyor-agent] Soft stop requested (discovery ExitPlanMode)\n");
      this.softStop();
    };

    bridge.onModeTransition = (newMode: AgentMode) => {
      const oldMode = this.mode.effectiveMode;
      process.stderr.write(`[conveyor-agent] Mode transition: ${oldMode} → ${newMode}\n`);
      this.connection.sendEvent({ type: "mode_transition", from: oldMode, to: newMode });
      this.mode.pendingModeRestart = true;
      this.connection.emitModeChanged(newMode);
      this.softStop();
    };

    // Keep-alive PTY: a human typing into the parked, idle Connected-TUI wakes a
    // passive turn (no-op on the SDK harness).
    bridge.onPassiveActivity(() => this.onPassiveTuiActivity());

    return bridge;
  }

  // ── Private helpers ────────────────────────────────────────────────

  private wireConnectionCallbacks(): void {
    this.connection.onMessage((msg) => this.injectMessage(msg));
    this.connection.onStop(() => this.stop());
    this.connection.onSoftStop(() => this.softStop());
    // After a reconnect the API process may be a fresh instance (deploy,
    // restart) whose PTY scrollback ring is empty — force a repaint so the
    // Connected-TUI terminal re-seeds instead of staying blank/hidden until
    // the CLI happens to draw again.
    this.connection.onReconnected = () => this.queryBridge?.forceRepaint();
    this.connection.onModeChange((data) => {
      const action = this.mode.handleModeChange(data.agentMode, this.taskContext);
      if (action.type === "start_auto") {
        this.connection.emitModeChanged(this.mode.effectiveMode);
        this.softStop();
      } else if (action.type === "restart_query") {
        // Server-initiated mode transition (e.g., CI-triggered review).
        // Clear claudeSessionId to prevent SDK resume across model changes
        // (reviewer agent may use a different model than the task agent).
        if (this.fullContext && action.newMode === "review") {
          this.fullContext.claudeSessionId = null;
        }
        // On building transition, re-fetch context to pick up the newly created
        // branch (branch creation is deferred until building mode).
        // Also refresh for "auto" post-exit (defense-in-depth if "auto" leaks through).
        if (
          this.fullContext &&
          (action.newMode === "building" ||
            (action.newMode === "auto" && this.mode.hasExitedPlanMode))
        ) {
          void this.refreshBranchForBuilding();
        }
        // Exit dormant idle so the review_trigger message that follows
        // is processed in the normal idle path (which handles interrupted
        // correctly without draining pendingMessages).
        this.completedThisTurn = false;
        this.connection.emitModeChanged(action.newMode);
        this.softStop();
      }
    });
    this.connection.onApiKeyUpdate((data) => {
      if (data.isSubscription) {
        process.env.CLAUDE_CODE_OAUTH_TOKEN = data.apiKey;
        delete process.env.ANTHROPIC_API_KEY;
        // The interactive TUI reads credentials from disk, not the env var —
        // refresh the synthesized file so the next PTY spawn (and any on-401
        // re-read by a live CLI) picks up the rotated subscription token.
        void ensureClaudeCredentials();
      } else {
        process.env.ANTHROPIC_API_KEY = data.apiKey;
        delete process.env.CLAUDE_CODE_OAUTH_TOKEN;
        // Drop our synthesized subscription credentials so a stale token
        // can't keep authenticating future TUI spawns over the API key.
        void removeConveyorCredentials();
      }
    });
    this.connection.onPullBranch(({ branch }) => {
      void handlePullBranch(this.config.workspaceDir, branch);
    });
    this.connection.onFinalizeSnapshot(() => void this.finalizeSnapshotNow());
  }

  /** Eager finalize snapshot triggered by the reconciler's sleep signal
   *  (session:finalizeSnapshot). Runs a full workspace capture and uploads it
   *  NOW so the sleep confirms on this snapshot instead of waiting for the
   *  ~2min periodic flush. Sidecar DB state is intentionally NOT captured — it
   *  is no longer durable across sleep/wake (#2623); only the agent's
   *  /workspace rides the snapshot. Best-effort: on failure the
   *  periodic/shutdown path stays the fallback. No-op on non-v3 pods. */
  private async finalizeSnapshotNow(): Promise<void> {
    const uploadUrl = process.env.CONVEYOR_SNAPSHOT_UPLOAD_URL;
    if (!uploadUrl || this.stopped) return;
    try {
      await finalizeForSleep({
        cwd: this.config.workspaceDir,
        branch: this.fullContext?.githubBranch ?? "",
        snapshotUploadUrl: uploadUrl,
        refreshToken: async () => {
          try {
            const res = await this.connection.call("refreshGithubToken", {
              sessionId: this.connection.sessionId,
            });
            return res.token;
          } catch {
            return undefined;
          }
        },
      });
      process.stderr.write("[conveyor-agent] Finalize snapshot uploaded on sleep signal\n");
    } catch {
      // best-effort — periodic/shutdown snapshot remains the fallback
    }
  }

  /** Proactively refresh the GitHub token before the 1-hour expiry. */
  private async refreshGithubToken(): Promise<void> {
    try {
      const res = await this.connection.call("refreshGithubToken", {
        sessionId: this.connection.sessionId,
      });
      await updateRemoteToken(this.config.workspaceDir, res.token);
      process.env.GITHUB_TOKEN = res.token;
      process.env.GH_TOKEN = res.token;
      process.stderr.write("[conveyor-agent] Proactively refreshed GitHub token\n");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      process.stderr.write(`[conveyor-agent] Warning: proactive token refresh failed: ${msg}\n`);
    }
  }

  /** Re-fetch task context to pick up a newly created branch and check it out. */
  private async refreshBranchForBuilding(): Promise<void> {
    try {
      const ctx = await this.connection.call("getTaskContext", {
        sessionId: this.sessionId,
        includeHistory: false,
      });
      if (ctx?.githubBranch && this.fullContext) {
        this.fullContext.githubBranch = ctx.githubBranch;
        await ensureOnTaskBranch(this.config.workspaceDir, ctx.githubBranch);
      }
    } catch {
      process.stderr.write(
        "[conveyor-agent] Warning: failed to refresh branch for building transition\n",
      );
    }
  }

  private async setState(status: AgentRunnerStatus): Promise<void> {
    this._state = status;
    // Mirror into the loop-lag buffer so the heartbeat worker reports the
    // runner's real status (same mapping as AgentConnection.sendHeartbeat).
    const loopStatus: LoopStatus = status === "idle" ? "idle" : "active";
    this.loopLag.setStatus(loopStatus);
    await this.connection.emitStatus(status);
    await this.callbacks.onStatusChange(status);
  }

  private async shutdown(finalState: AgentRunnerStatus): Promise<void> {
    process.stderr.write(`[conveyor-agent] Shutdown: reason=${finalState}\n`);
    this.connection.sendEvent({ type: "shutdown", reason: finalState });
    this.portDiscovery.stop();
    this.loopLag.stop();
    this.lifecycle.destroy();
    // Keep-alive PTY: tear down the parked/active CLI process before we drop the
    // socket, so the ended signal (ring teardown → tab hides) can go out.
    try {
      await this.queryBridge?.dispose();
    } catch {
      /* best effort */
    }
    try {
      await this.setState(finalState);
    } catch {
      // Best effort — when stop() already tore down the connection the status
      // RPC rejects. A stopped agent should still finish run() cleanly so the
      // process exits 0 instead of taking the error path.
    }
    this.connection.disconnect();
    this._finalState = finalState;
  }

  private _finalState: AgentRunnerStatus | null = null;

  /** The final status after run() completes. Use to determine exit code. */
  get finalState(): AgentRunnerStatus | null {
    return this._finalState;
  }

  private buildTaskContextSnapshot(): Record<string, unknown> {
    return {
      isParentTask: this.fullContext?.isParentTask ?? false,
      status: this.taskContext?.status,
      taskTitle: this.fullContext?.title,
      hasExistingPR: !!this.fullContext?.githubPRUrl,
      hasExistingSession: !!this.fullContext?.claudeSessionId,
      chatHistoryLength: this.fullContext?.chatHistory?.length ?? 0,
      tagIds: this.fullContext?.taskTagIds ?? [],
    };
  }

  private buildInitializationContext(): Record<string, unknown> {
    return {
      mode: this.mode.effectiveMode,
      runnerMode: this.config.runnerMode ?? "task",
      sessionId: this.sessionId,
      ...this.buildTaskContextSnapshot(),
      model: this.taskContext?.model,
      isAuto: this.config.isAuto ?? false,
      subscriptionKeyLabel: process.env.CONVEYOR_SUBSCRIPTION_KEY_LABEL ?? null,
    };
  }

  private logInitialization(): void {
    const context = this.buildInitializationContext();
    process.stderr.write(`[conveyor-agent] Initialized: ${JSON.stringify(context)}\n`);
    this.connection.sendEvent({ type: "session_manifest", ...context });
  }
}
